import { BookOpen, HelpCircle, Plus, X } from 'lucide-react';
import { useEffect } from 'react';
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { CarrierNameSelect } from '@/components/carrier-directory/CarrierNameSelect';
import { CarrierPocketbook } from '@/components/carrier-directory/CarrierPocketbook';
import { DirectoryHandlesPreview } from '@/components/carrier-directory/DirectoryHandlesPreview';
import type { ContactHandle as DirectoryHandle } from '@/components/carrier-directory/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useListDivisionsQuery } from '@/services/api/carriersApiService';
import {
  CoverageCategory,
  CoverageLiabilityStatus,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';

import { CoverageFormData } from './types';

interface BasicInfoTabProps {
  register: UseFormRegister<CoverageFormData>;
  setValue: UseFormSetValue<CoverageFormData>;
  watch: UseFormWatch<CoverageFormData>;
  insuredFields: FieldArrayWithId<CoverageFormData, 'named_insured', 'id'>[];
  appendInsured: UseFieldArrayAppend<CoverageFormData, 'named_insured'>;
  removeInsured: UseFieldArrayRemove;
  policyIdFields: FieldArrayWithId<
    CoverageFormData,
    'policy_identifiers',
    'id'
  >[];
  appendPolicyId: UseFieldArrayAppend<CoverageFormData, 'policy_identifiers'>;
  removePolicyId: UseFieldArrayRemove;
}

const FieldWithTooltip = ({
  label,
  tooltip,
  required = false,
  children,
}: {
  label: string;
  tooltip: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <Label className={`${required ? 'font-semibold' : ''}`}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
    {children}
  </div>
);

export const BasicInfoTab = ({
  register,
  setValue,
  watch,
  insuredFields,
  appendInsured,
  removeInsured,
  policyIdFields,
  appendPolicyId,
  removePolicyId,
}: BasicInfoTabProps) => {
  const selectedCategory = watch('category');
  const selectedPolicyType = watch('policy_type');
  const selectedLiabilityStatus = watch('liability_status');
  const selectedCarrierId = watch('carrier_v2_id');
  const selectedDivisionId = watch('carrier_division_id');
  const carrierName = watch('carrier.name');
  const insuredValues = watch('named_insured');
  const policyIdentifiers = watch('policy_identifiers');

  useEffect(() => {
    return () => {
      // eslint-disable-next-line no-console
      console.log('BasicInfoTab: unmounted');
    };
  }, []);
  const { data: divisions = [] } = useListDivisionsQuery(
    { carrierId: selectedCarrierId || '' },
    { skip: !selectedCarrierId },
  );

  return (
    <div className="space-y-6">
      {/* Header with guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          Getting Started
        </h3>
        <p className="text-sm text-blue-800">
          Start with the essential information below. You can always add more
          details in the other tabs later.
        </p>
      </div>

      {/* Essential Information Section */}
      <Card className="border-2 border-blue-100">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
            Essential Information
            <span className="text-xs font-normal bg-blue-100 text-blue-800 px-2 py-1 rounded">
              Required
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Coverage Category */}
            <FieldWithTooltip
              label="Coverage Category"
              tooltip="Choose who this policy belongs to: First Party (client), Third Party (at-fault), Not-Liable Involved Party (involved but not at fault), or Health Insurance."
              required
            >
              <Select
                value={selectedCategory || undefined}
                onValueChange={(value) =>
                  setValue('category', value as CoverageCategory)
                }
              >
                <SelectTrigger
                  className={!selectedCategory ? 'border-red-300' : ''}
                >
                  <SelectValue placeholder="Select coverage category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CoverageCategory.FIRST_PARTY}>
                    <div className="space-y-1">
                      <div className="font-medium">
                        First Party (Client&apos;s Auto)
                      </div>
                      <div className="text-xs text-gray-500">
                        MedPay, PIP, UM/UIM, Collision
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value={CoverageCategory.FIRST_PARTY_HEALTH}>
                    <div className="space-y-1">
                      <div className="font-medium">Health Insurance</div>
                      <div className="text-xs text-gray-500">
                        Primary health coverage
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value={CoverageCategory.THIRD_PARTY}>
                    <div className="space-y-1">
                      <div className="font-medium">Third Party (At-fault)</div>
                      <div className="text-xs text-gray-500">
                        Bodily injury, property damage
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem
                    value={CoverageCategory.NOT_LIABLE_INVOLVED_PARTY}
                  >
                    <div className="space-y-1">
                      <div className="font-medium">
                        Not-Liable Involved Party
                      </div>
                      <div className="text-xs text-gray-500">
                        Vehicle/person involved but currently not at fault
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value={CoverageCategory.UNCLEAR}>
                    <div className="space-y-1">
                      <div className="font-medium">Unclear</div>
                      <div className="text-xs text-gray-500">
                        Need more information
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value={CoverageCategory.PROPERTY}>
                    <div className="space-y-1">
                      <div className="font-medium">Property</div>
                      <div className="text-xs text-gray-500">
                        Premises liability property
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value={CoverageCategory.WORKERS_COMPENSATION}>
                    <div className="space-y-1">
                      <div className="font-medium">Workers Compensation</div>
                      <div className="text-xs text-gray-500">
                        Workplace injury coverage
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </FieldWithTooltip>

            {/* Policy Type */}
            <FieldWithTooltip
              label="Policy Type"
              tooltip="The type of insurance policy. Auto is most common for personal injury cases."
              required
            >
              <Select
                value={selectedPolicyType || undefined}
                onValueChange={(value) =>
                  setValue('policy_type', value as PolicyType)
                }
              >
                <SelectTrigger
                  className={!selectedPolicyType ? 'border-red-300' : ''}
                >
                  <SelectValue placeholder="Select policy type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PolicyType.AUTO}>
                    <div className="space-y-1">
                      <div className="font-medium">Auto Insurance</div>
                      <div className="text-xs text-gray-500">
                        Most common for PI cases
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value={PolicyType.HEALTH}>
                    Health Insurance
                  </SelectItem>
                  <SelectItem value={PolicyType.HOMEOWNERS}>
                    Homeowners
                  </SelectItem>
                  <SelectItem value={PolicyType.COMMERCIAL}>
                    Commercial
                  </SelectItem>
                  <SelectItem value={PolicyType.UMBRELLA}>
                    Umbrella Policy
                  </SelectItem>
                  <SelectItem value={PolicyType.GENERAL_LIABILITY}>
                    General Liability
                  </SelectItem>
                  <SelectItem value={PolicyType.OTHER}>Other</SelectItem>
                </SelectContent>
              </Select>
            </FieldWithTooltip>
          </div>

          {/* Liability Status */}
          <FieldWithTooltip
            label="Liability Status"
            tooltip="The determination of liability for this coverage. Set whether the insurance carrier has accepted, rejected, or partially accepted liability."
          >
            <Select
              value={selectedLiabilityStatus || undefined}
              onValueChange={(value) =>
                setValue('liability_status', value as CoverageLiabilityStatus)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Set liability status (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CoverageLiabilityStatus.ACCEPTED}>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Accepted</span>
                  </div>
                </SelectItem>
                <SelectItem value={CoverageLiabilityStatus.REJECTED}>
                  <div className="flex items-center gap-2">
                    <span className="text-red-600">✗</span>
                    <span>Rejected</span>
                  </div>
                </SelectItem>
                <SelectItem value={CoverageLiabilityStatus.PARTIALLY_ACCEPTED}>
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-600">◐</span>
                    <span>Partially Accepted</span>
                  </div>
                </SelectItem>
                <SelectItem value={CoverageLiabilityStatus.LIABILITY_UNKNOWN}>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">?</span>
                    <span>Unknown</span>
                  </div>
                </SelectItem>
                <SelectItem value={CoverageLiabilityStatus.UNDER_INVESTIGATION}>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600">⚑</span>
                    <span>Under Investigation</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </FieldWithTooltip>

          {/* Denial Reason — shown when liability is REJECTED or PARTIALLY_ACCEPTED */}
          {(selectedLiabilityStatus === CoverageLiabilityStatus.REJECTED ||
            selectedLiabilityStatus ===
              CoverageLiabilityStatus.PARTIALLY_ACCEPTED) && (
            <FieldWithTooltip
              label="Denial Reason"
              tooltip="Explain why the insurance carrier denied or partially accepted liability for this coverage."
            >
              <Textarea
                placeholder="Enter the reason for denial..."
                {...register('denial_reason')}
                className="min-h-[80px]"
              />
            </FieldWithTooltip>
          )}

          {/* Named Insured / Property Address (for premises) */}
          <FieldWithTooltip
            label={
              selectedCategory === CoverageCategory.PROPERTY
                ? 'Property Address'
                : 'Named Insured'
            }
            tooltip={
              selectedCategory === CoverageCategory.PROPERTY
                ? 'The address of the property where the incident occurred.'
                : 'The person(s) or entity listed as the policyholder on the insurance policy.'
            }
            required
          >
            <div className="space-y-3">
              {insuredFields.map((field, index) => (
                <div key={field.id} className="flex gap-2">
                  <Input
                    placeholder={
                      selectedCategory === CoverageCategory.PROPERTY
                        ? index === 0
                          ? 'Property address'
                          : 'Additional property address'
                        : index === 0
                          ? 'Primary insured name'
                          : 'Additional insured name'
                    }
                    {...register(`named_insured.${index}.name`)}
                    className="flex-1"
                  />
                  {insuredFields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeInsured(index)}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => appendInsured({ name: '' })}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-1" />
                {selectedCategory === CoverageCategory.PROPERTY
                  ? 'Add Another Property'
                  : 'Add Another Insured'}
              </Button>
            </div>
          </FieldWithTooltip>
        </CardContent>
      </Card>

      {/* Carrier Information - show different UI for premises vs. other categories */}
      {selectedCategory === CoverageCategory.PROPERTY ? (
        /* Premises: Separate sections for insurance carrier and property owner/manager */
        <>
          {/* Insurance Carrier Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                Insurance Carrier
                <span className="text-xs font-normal bg-gray-100 text-gray-600 px-2 py-1 rounded">
                  Optional
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FieldWithTooltip
                label="Insurance Carrier Name"
                tooltip="The insurance company providing coverage for this property (e.g., General Liability Insurance). Leave blank if unknown."
              >
                <Input
                  placeholder="e.g., General Liability Insurance, State Farm"
                  {...register('carrier.name')}
                />
              </FieldWithTooltip>

              <FieldWithTooltip
                label="Insurance Carrier Address"
                tooltip="The mailing address for the insurance carrier, if known."
              >
                <Input
                  placeholder="e.g., PO Box 1234, Anytown, ST 12345"
                  {...register('carrier.address')}
                />
              </FieldWithTooltip>
            </CardContent>
          </Card>

          {/* Property Owner/Manager Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                Property Owner / Manager
                <span className="text-xs font-normal bg-gray-100 text-gray-600 px-2 py-1 rounded">
                  Optional
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Use the <strong>Contacts</strong> tab to add detailed property
                  owner and manager contacts, or the &quot;Research Property
                  Owners&quot; step to discover them.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        /* Standard: Carrier directory integration */
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Insurance Carrier
              <span className="text-xs font-normal bg-gray-100 text-gray-600 px-2 py-1 rounded">
                Optional
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FieldWithTooltip
              label="Carrier Name"
              tooltip="The insurance company that issued this policy (e.g., State Farm, GEICO, Allstate)."
            >
              <div className="flex items-center gap-2">
                <CarrierNameSelect
                  value={watch('carrier.name')}
                  onChange={(name) => {
                    setValue('carrier.name', name || null, {
                      shouldDirty: true,
                    });
                    // When carrier name is cleared, also unlink the carrier directory references and handles
                    if (!name) {
                      setValue('carrier_v2_id', null, { shouldDirty: true });
                      setValue('carrier_division_id', null, {
                        shouldDirty: true,
                      });
                      setValue('carrier.handles', null, { shouldDirty: true });
                      setValue('carrier.address', null, { shouldDirty: true });
                    }
                  }}
                  onSelectDirectory={(carrierId, divisionId) => {
                    setValue('carrier_v2_id', carrierId, {
                      shouldDirty: true,
                    });
                    setValue('carrier_division_id', divisionId || null, {
                      shouldDirty: true,
                    });
                  }}
                />
                <CarrierPocketbook
                  trigger={
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors border border-blue-200 hover:border-blue-300"
                      title="Add Carrier Contact"
                    >
                      <BookOpen className="w-4.5 h-4.5" />
                      <span className="text-sm font-medium">Add</span>
                    </button>
                  }
                  actions={['use', 'copy']}
                  onAttachSelected={(_, selected: DirectoryHandle[]) => {
                    const toValue = (h: DirectoryHandle): string => {
                      if (h.value) return h.value;
                      const parts = [
                        h.addressLine1,
                        h.city,
                        h.state,
                        h.zip,
                        h.country,
                      ].filter(Boolean);
                      return parts.join(', ');
                    };
                    const mapped =
                      selected?.map((h) => ({
                        handle_type: h.handleType,
                        value: toValue(h),
                        name: h.name,
                      })) || [];
                    const existing = watch('carrier.handles') || [];
                    setValue(
                      'carrier.handles',
                      [...existing, ...mapped].filter(Boolean),
                      { shouldDirty: true },
                    );
                  }}
                />
              </div>
              {selectedDivisionId ? (
                <div className="mt-2 text-xs text-gray-700">
                  Selected division:{' '}
                  <span className="font-medium">
                    {divisions.find((d) => d.id === selectedDivisionId)
                      ?.divisionName || 'Unknown'}
                  </span>
                </div>
              ) : null}
            </FieldWithTooltip>

            {/* Carrier Address */}
            <FieldWithTooltip
              label="Carrier Address"
              tooltip="The headquarters or main office address of the insurance carrier, if known."
            >
              <Input
                placeholder="e.g., 1234 Main St, Anytown, ST 12345"
                {...register('carrier.address')}
              />
            </FieldWithTooltip>

            {/* Read-only handles preview for selected directory carrier/division */}
            {(() => {
              const carrierId = watch('carrier_v2_id');
              const divisionId = watch('carrier_division_id');
              return (
                <DirectoryHandlesPreview
                  carrierId={carrierId || ''}
                  divisionId={divisionId || ''}
                />
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Policy Identifiers - Optional */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Policy Identifiers
            <span className="text-xs font-normal bg-gray-100 text-gray-600 px-2 py-1 rounded">
              Optional
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FieldWithTooltip
            label="Policy Numbers & IDs"
            tooltip="Any identifying numbers for this policy - policy number, member ID, group number, etc."
          >
            <div className="space-y-3">
              {policyIdFields.map((field, index) => (
                <div key={field.id} className="flex gap-2">
                  <Select
                    value={
                      watch(`policy_identifiers.${index}.identifier_type`) ||
                      undefined
                    }
                    onValueChange={(value) =>
                      setValue(
                        `policy_identifiers.${index}.identifier_type`,
                        value as IdentifierType,
                      )
                    }
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="ID type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={IdentifierType.POLICY_NUMBER}>
                        Policy Number
                      </SelectItem>
                      <SelectItem value={IdentifierType.MEMBER_ID}>
                        Member ID
                      </SelectItem>
                      <SelectItem value={IdentifierType.GROUP_NUMBER}>
                        Group Number
                      </SelectItem>
                      <SelectItem value={IdentifierType.OTHER}>
                        Other
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Enter the identifier value"
                    {...register(
                      `policy_identifiers.${index}.identifier_value`,
                    )}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePolicyId(index)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {policyIdFields.length === 0 ? (
                <div className="text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-2">
                    No policy identifiers added yet
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      appendPolicyId({
                        identifier_type: IdentifierType.POLICY_NUMBER,
                        identifier_value: '',
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Policy Identifier
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    appendPolicyId({
                      identifier_type: IdentifierType.POLICY_NUMBER,
                      identifier_value: '',
                    })
                  }
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Another Identifier
                </Button>
              )}
            </div>
          </FieldWithTooltip>
        </CardContent>
      </Card>
    </div>
  );
};
